import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import SectionHeader from 'components/ui/typography/SectionHeader';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  equipmentService,
  examService,
  laboratoryService,
  workstationService
} from 'shared/services';
import { activityService } from 'features/cost-analysis/team/activities/api/activity.service';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { sectorService } from 'features/settings/topology/api/sector.service';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';

type LayerType = {
  id: number;
  model: string;
};

export default function AddActivityDialog({
  expertiseId,
  open,
  setOpen,
  callback
}: {
  expertiseId: number;
  open: boolean;
  setOpen: (value) => any;
  callback?: any;
}) {
  const { t } = useTranslation();

  const [options, setOptions] = useState<Option[]>([]);
  const [mode, setMode] = useState<LayerType>({ id: null, model: null });
  const [modes, setModes] = useState<LayerType[]>([]);

  useEffect(() => {
    activityService.getLayerOptions().then(setModes);
  }, []);

  useEffect(() => {
    if (!mode) return;
    switch (mode.model) {
      case 'laboratory':
        laboratoryService.getAllBaseInformation().then(setOptions);
        break;
      case 'operationalunit':
        operationalUnitService.getAllBaseInformation().then(setOptions);
        break;
      case 'sector':
        sectorService.getAllBaseInformation().then(setOptions);
        break;
      case 'workstation':
        workstationService.getAllBaseInformation().then(setOptions);
        break;
      case 'equipment':
        equipmentService.getAllBaseInformation().then(setOptions);
        break;
      case 'exam':
        examService.getAllBaseInformation().then(setOptions);
        break;
      default:
        setOptions([]);
    }
  }, [mode]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (values) => {
    activityService.add({ ...values, content_type: mode.id, expertise: expertiseId }).then(() => {
      toast.success('Attività aggiunta');
      handleClose();
      if (callback) callback();
    });
  };

  const handleModeChange = (event: SelectChangeEvent<number>) => {
    setMode(modes.find((m) => +m.id == +event.target.value));
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={handleClose}
      title={t('entities.activity.add_dialog.title')}
      subTitle={t('entities.activity.add_dialog.subtitle')}
      submitLabel={t('actions.save', 'Salva')}
      cancelLabel={t('actions.reset', 'Annulla')}
      maxWidth="md"
      submitButtonProps={{
        type: 'submit',
        form: 'addCostLink'
      }}>
      <EnhancedForm
        onSubmit={onSubmit}
        formId="addCostLink"
        hideControls
        autoStyleLayout
        columns={6}>
        <SectionHeader
          title={t('entities.activity.add_dialog.sections.general')}
          subtitle={t('entities.activity.add_dialog.sections.general_subtitle')}
          gridSpan
        />
        <IdentityField
          gridSpan={6}
          name="name"
          component={TextFieldAdapter}
          label={t('entities.activity.name')}
          type="text"
        />
        <IdentityField
          gridSpan={6}
          name="description"
          component={TextFieldAdapter}
          label={t('global.description')}
          type="text"
        />
        <SectionHeader
          title={t('entities.activity.add_dialog.sections.commitment')}
          subtitle={t('entities.activity.add_dialog.sections.commitment_subtitle')}
          gridSpan
        />
        <IdentityField
          gridSpan={2}
          name="monthly_hours"
          component={TextFieldAdapter}
          label={t('entities.activity.monthly_hours')}
          type="text"
        />
        <IdentityField
          gridSpan={2}
          name="output_number"
          component={TextFieldAdapter}
          label={t('entities.activity.output_number')}
          type="text"
        />
        <IdentityField
          gridSpan={2}
          name="monthly_percentage"
          component={TextFieldAdapter}
          label={t('entities.activity.monthly_percentage')}
          type="text"
        />
        <SectionHeader
          title={t('entities.activity.add_dialog.sections.validity')}
          subtitle={t('entities.activity.add_dialog.sections.validity_subtitle')}
          gridSpan
        />
        <IdentityField
          gridSpan={3}
          name="date_start"
          component={DatePickerAdapter}
          label={t('global.date_start')}
          type="text"
        />
        <IdentityField
          gridSpan={3}
          name="date_end"
          component={DatePickerAdapter}
          label={t('global.date_end')}
          type="text"
        />
        <SectionHeader
          title={t('entities.activity.add_dialog.sections.area')}
          subtitle={t('entities.activity.add_dialog.sections.area_subtitle')}
          gridSpan
        />
        <div style={{ gridColumn: '1 / -1' }}>
          <Select
            label="Layer"
            fullWidth
            value={mode.id}
            onChange={handleModeChange}
            className="mb-4">
            <MenuItem value="">
              <em>---</em>
            </MenuItem>
            {modes &&
              modes.map((mode) => (
                <MenuItem key={mode.id} value={mode.id}>
                  {mode.model}
                </MenuItem>
              ))}
          </Select>
        </div>
        <SectionHeader
          title={t('entities.activity.add_dialog.sections.source', 'Fonti di costo')}
          subtitle={t('entities.activity.add_dialog.sections.source_subtitle')}
          gridSpan
        />
        {options && (
          <IdentityField
            gridSpan={6}
            name="object_id"
            component={AutocompleteAdapter}
            options={options}
            label={t('global.cost-source')}
          />
        )}
      </EnhancedForm>
    </EnhancedDialog>
  );
}
